import React, { useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { setUser } from "../../../redux/slices/authSlice";
import { authToken } from "../../../utils/storage/storage";
import { ReactComponent as Refresh } from "../../../assets/images/icons/Refresh.svg";
import { ReactComponent as Logout } from "../../../assets/images/sidebar/Logout.svg";
import { ReactComponent as Feedback } from "../../../assets/images/svg/feedback.svg";
import { ReactComponent as Bars } from "../../../assets/images/icons/Bars.svg";
import { ControlledMenu } from "@szhsin/react-menu";
import emptyProfile from "../../../assets/images/sidebar/EmptyProfile.svg";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { setMobileSidebarStatus } from "../../../redux/slices/layoutSlice";
import { post, responseValidator } from "../../../scripts/api";
import API from "../../../urls/api";
import PATH from "../../../urls/path";
import info from "../../../assets/images/icons/info-q.svg";
import rules from "../../../assets/images/sidebar/task-square.svg";
import { setContactSynchronize } from "../../../redux/slices/contactSlice";
import { syncFileManager, updateQuota } from "../../../utils/component/fileManagers/fileManager.function";
import Search from "./search/search.index";
import { zIndexs } from "../../../interfaces/environment";
import { getUserProfile, getWebEnvConfig, isTouchScreen } from "../../../scripts/helpers";
import { ENVIRONMENT } from "../../../environment";
import LogoWithName from "../../../utils/component/logo/logoWithName.index";
import { shallowEqual } from "react-redux";
import Notifications from "./notification/notifications.index";

export default function Header(): JSX.Element {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector(({ auth: { user } }) => ({ user }), shallowEqual);
    const { Synchronize } = useAppSelector(({ contact: { Synchronize } }) => ({ Synchronize }), shallowEqual);
    const [menuStatus, setMenuStatus] = useState(false);
    const [menuStatusInfo, setMenuStatusInfo] = useState(false);
    const ref = useRef(null);
    const refInfo = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    const handleLogout = () => {
        return new Promise((resolve) => {
            post(
                (data, status) => {
                    if (responseValidator(status)) {
                        dispatch(setUser(null));
                        authToken.remove();
                        window.location.replace(`${getWebEnvConfig("landingBaseUrl")}auth/login`);
                    }
                    resolve(true);
                },
                API.auth.logout(),
                { refresh: authToken.get()?.refresh }
            );
        });
    };

    return (
        <React.Fragment>
            {(menuStatus || menuStatusInfo) && (
                <div className={`z-[100] w-screen h-screen bg-overlay absolute top-0 left-0 animate__animated animate__faster animate__fadeIn`} />
            )}
            {isTouchScreen() ? (
                <div className="w-full rounded-[20px] mb-4">
                    <div className="flex items-center justify-between">
                        <Bars className="cursor-pointer" onClick={() => dispatch(setMobileSidebarStatus(true))} />
                        <Link to={PATH.dashboard}>
                            <LogoWithName png />
                        </Link>
                        <Notifications />
                    </div>
                    <div className="flex gap-2 mt-4">
                        <Search />
                    </div>
                </div>
            ) : (
                <div className="w-full mb-4">
                    <div className="flex items-center justify-between gap-4">
                        <Search />
                        <div className="flex items-center gap-6">
                            <div className="cursor-pointer" ref={refInfo} onClick={() => setMenuStatusInfo(true)}>
                                <img data-tip="راهنما" src={info} alt="info" className="w-[30px] h-[30px]" />
                                <ControlledMenu
                                    state={menuStatusInfo ? "open" : "closed"}
                                    menuClassName={`!py-0 !rounded-[16px] !bg-white !top-[20px] !z-${zIndexs.otherModals}`}
                                    arrow={true}
                                    anchorRef={refInfo}
                                    onClose={() => setMenuStatusInfo(false)}
                                    transition={true}
                                >
                                    <div className="flex flex-col divide-y px-4 py-2">
                                        <a
                                            href={`${getWebEnvConfig("landingBaseUrl")}rules`}
                                            target="_blank"
                                            className="py-2 flex items-center gap-2"
                                            rel="noreferrer"
                                        >
                                            <img src={rules} alt="info" className="w-[18px] h-[18px]" />
                                            <span className="text-secondary-700 text-[14px]">قوانین</span>
                                        </a>
                                        <a
                                            href={`${getWebEnvConfig("landingBaseUrl")}help`}
                                            target="_blank"
                                            className="py-2 flex items-center gap-2"
                                            rel="noreferrer"
                                        >
                                            <img src={info} alt="info" className="w-[18px] h-[18px]" />
                                            <span className="text-secondary-700 text-[14px]">راهنمای استفاده از ابرهمراهی</span>
                                        </a>
                                    </div>
                                </ControlledMenu>
                            </div>
                            <div
                                data-tip="به‌روزرسانی"
                                className={`cursor-pointer`}
                                onClick={() => {
                                    getUserProfile();
                                    if (location.pathname === PATH.contact) {
                                        updateQuota();
                                        dispatch(setContactSynchronize(!Synchronize));
                                    } else syncFileManager(true);
                                }}
                            >
                                <Refresh className="fill-secondary-500 w-[30px] h-[30px]" />
                            </div>
                            <div className={`cursor-pointer`}>
                                <Notifications />
                            </div>
                            <div className="cursor-pointer" onClick={() => navigate(PATH.feedback)} data-tip="ثبت بازخورد">
                                <Feedback className="w-[30px] h-[30px] fill-secondary-500" />
                            </div>
                            <div className="cursor-pointer" ref={ref} onClick={() => setMenuStatus(true)}>
                                <img
                                    src={user?.picture_url ? user.picture_url : emptyProfile}
                                    alt="user"
                                    className="w-[30px] h-[30px] rounded-full object-cover"
                                />
                            </div>
                            <ControlledMenu
                                state={menuStatus ? "open" : "closed"}
                                menuClassName={`!py-0 !rounded-[16px] !bg-white !top-[32px] !z-${zIndexs.otherModals}`}
                                arrow={true}
                                anchorRef={ref}
                                onClose={() => setMenuStatus(false)}
                                transition={true}
                            >
                                <div className="flex flex-col items-center justify-center divide-y">
                                    <div className="!px-8 !py-4 flex flex-col items-center justify-center">
                                        <img
                                            className="block w-[48px] h-[48px] rounded-full object-cover"
                                            src={user?.picture_url ? user.picture_url : emptyProfile}
                                            alt={user?.name}
                                        />
                                        <span className="text-primary-600 text-[14px] font-bold mt-2 whitespace-nowrap overflow-hidden text-ellipsis w-[100px] text-center">
                                            {user?.name ? user.name : `کاربر ${ENVIRONMENT.brandName}`}
                                        </span>
                                        <span className="text-[12px] text-secondary-500 mt-2">{user?.phone}</span>
                                    </div>
                                    <div
                                        className="!px-8 !py-4 flex items-center gap-4 cursor-pointer"
                                        onClick={() => {
                                            handleLogout();
                                            setMenuStatus(false);
                                        }}
                                    >
                                        <Logout />
                                        <span className="text-[14px] text-red-500">خروج از حساب کاربری</span>
                                    </div>
                                </div>
                            </ControlledMenu>
                        </div>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
}
